import { ArrowRight, ChevronDown, ExternalLink, Sparkles, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ContactLink } from './ContactLink';
import { itemsToShow, releases as allReleases, type Release } from '../../whats-new/releases';
import { contacts, PREFACE_RELEASE } from '../../whats-new/preface';
import { track } from '../../lib/track';

/**
 * `first` : opened by the application itself after a login, on the releases the user has not
 * seen yet. `history` : opened on purpose from the menu or a « NEW » tooltip, on everything.
 * The panel looks the same either way ; the mode only drives the usage events.
 */
export type WhatsNewMode = 'first' | 'history';

type Props = {
  open: boolean;
  onClose: () => void;
  /** Releases to present, newest first. Default: the whole history. */
  releases?: Release[];
  mode?: WhatsNewMode;
};

/** An item link to a page outside the application (a guide on claude.ai…), not a route of the app. */
const isExternal = (href: string) => /^https?:\/\//.test(href);

function Preface() {
  const [lead, second] = contacts.owners;
  return (
    <div className="space-y-3 border-b border-neutral-100 px-6 py-5 text-sm leading-relaxed text-neutral-700">
      <p>
        Plusieurs nouveautés arrivent d'un coup, et ce n'est qu'un début : l'application va maintenant s'améliorer
        en continu.
      </p>
      <p>
        Un retour, une idée, un bug, en particulier sur les derniers ajouts ? Écrivez à <ContactLink {...lead} /> ou
        à <ContactLink {...second} />, avec <ContactLink {...contacts.sponsor} /> en copie, garant business de
        l'application.
      </p>
      <p>
        Le développement a aussi été modernisé : davantage de profils peuvent être mobilisés sur vos retours, et si
        vous proposez une évolution, vous pourrez la tester dans un environnement de test avant sa mise en ligne. On
        vous expliquera comment le moment venu.
      </p>
    </div>
  );
}

export function WhatsNewModal({ open, onClose, releases = allReleases, mode = 'history' }: Props) {
  const listRef = useRef<HTMLDivElement>(null);
  // Scroll affordance: a hint and a fade at the bottom until the user has scrolled or everything fits.
  const [scrolled, setScrolled] = useState(false);
  const [overflowing, setOverflowing] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    setScrolled(false);
    const el = listRef.current;
    if (!el) return;
    const measure = () => setOverflowing(el.scrollHeight > el.clientHeight + 4);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [open, releases, mode]);

  if (!open || releases.length === 0) return null;

  const first = mode === 'first';
  // The welcome belongs to the release that introduced the panel: shown as long as it is in the list.
  const withPreface = releases.some((r) => r.version === PREFACE_RELEASE);
  // Newest release first, as `releases` already is: the latest additions are what people open the
  // panel for. No version numbers (they mean nothing to users).
  const count = releases.reduce((n, r) => n + itemsToShow(r, releases).length, 0);
  const single = releases.length === 1;
  const kicker = single ? `Nouveautés · version ${releases[0].version}` : 'Nouveautés · depuis la dernière fois';
  const title = withPreface
    ? 'Bonne nouvelle : l’application évolue'
    : single
      ? releases[0].title
      : 'Ce qui a changé depuis la dernière fois';

  const onScroll = () => {
    const el = listRef.current;
    if (!el || scrolled) return;
    if (el.scrollTop > 8) {
      setScrolled(true);
      if (first) track('whats-new', 'scroll', 'whats-new', { count });
    }
  };

  const showHint = overflowing && !scrolled;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/40 p-4 backdrop-blur-sm animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="whats-new-title"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative shrink-0 bg-gradient-to-br from-primary-600 to-primary-800 px-6 py-5 text-white">
          <button
            type="button"
            onClick={onClose}
            className="absolute right-3 top-3 rounded-full p-1.5 text-white/80 hover:bg-white/15 hover:text-white"
            aria-label="Fermer"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/80">
            <Sparkles className="h-4 w-4" />
            {kicker}
          </div>
          <h2 id="whats-new-title" className="mt-1.5 text-xl font-semibold leading-snug">
            {title}
          </h2>
        </div>

        <div className="relative flex min-h-0 flex-1 flex-col">
          <div ref={listRef} onScroll={onScroll} className="scrollbar-custom min-h-0 flex-1 overflow-y-auto">
            {withPreface && <Preface />}
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-neutral-200 bg-white/95 px-6 py-2.5 backdrop-blur">
              <span className="text-sm font-semibold text-neutral-900">
                Ce qui a changé
                <span className="ml-2 text-xs font-medium text-neutral-500">
                  {count} nouveauté{count > 1 ? 's' : ''}
                </span>
              </span>
            </div>
            {releases.map((release) => (
              <section key={release.version} aria-label={`Version ${release.version}`}>
                <h3 className="border-b border-neutral-100 bg-neutral-50 px-6 py-2 text-sm font-semibold text-neutral-800">
                  {release.title}
                </h3>
                <ul className="divide-y divide-neutral-100">
                  {itemsToShow(release, releases).map((item) => (
                    <li key={item.title} className="px-6 py-4">
                      <p className="text-sm font-semibold text-neutral-900">{item.title}</p>
                      <p className="mt-1 text-sm text-neutral-600">{item.text}</p>
                      {item.href && isExternal(item.href) && (
                        // A page outside the app (a guide): new tab, the panel stays open. The event keeps
                        // the address without its query string, which may carry a share key.
                        <a
                          href={item.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() =>
                            track('whats-new', 'discover', 'whats-new', {
                              href: item.href!.split('?')[0],
                              version: release.version,
                            })
                          }
                          className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:text-primary-900"
                        >
                          Découvrir <ExternalLink className="h-4 w-4" />
                        </a>
                      )}
                      {item.href && !isExternal(item.href) && (
                        <Link
                          to={item.href}
                          onClick={() => {
                            track('whats-new', 'discover', 'whats-new', { href: item.href, version: release.version });
                            onClose();
                          }}
                          className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:text-primary-900"
                        >
                          Découvrir <ArrowRight className="h-4 w-4" />
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
          {showHint && (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 flex h-16 items-end justify-center bg-gradient-to-t from-white via-white/80 to-transparent pb-2">
              <span className="flex items-center gap-1 rounded-full border border-primary-200 bg-white px-3 py-1 text-xs font-medium text-primary-700 shadow-sm">
                Faites défiler <ChevronDown className="h-4 w-4 animate-bounce" />
              </span>
            </div>
          )}
        </div>

        <div className="flex shrink-0 justify-end border-t border-neutral-100 bg-neutral-50 px-6 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
          >
            C'est noté
          </button>
        </div>
      </div>
    </div>
  );
}
